// Step 5 — Review & Submit.
// Diffs the live wizard state against the prefill snapshot and groups
// pending changes by section. Submit button is the canonical save action
// for the whole wizard. Real submission wiring lives in build-order step 11;
// for now the button calls a placeholder that just logs the payload.

import { useMemo } from "react";

import { STATES } from "../../lib/stateOptions";
import { useFormStore } from "../../store";
import type { ContactRow, DocumentRow, SupplierFields } from "../../types";

// Field-by-field labels for the supplier diff. Hidden fields (id) are excluded.
const SUPPLIER_FIELD_LABELS: Partial<Record<keyof SupplierFields, string>> = {
  new_name: "Company Name",
  new_addressline1: "Address Line 1",
  new_addressline2: "Address Line 2",
  new_city: "City",
  new_stateprovince: "State / Province",
  new_zip: "ZIP / Postal Code",
  new_country: "Country",
  new_phone: "Business Phone",
  new_fax: "Business Fax",
  emailaddress: "Business Email",
  new_website: "Business Website",
  new_logourl: "Logo",
};

const PRODUCT_FIELD_LABELS: Partial<Record<keyof SupplierFields, string>> = {
  new_servicearea: "Service Area / Country",
  new_about: "About",
  new_discountdetails: "Benefits",
};

const SERVICE_AREA_LABEL: Record<number, string> = {
  100000000: "North America",
  100000001: "United States Only",
  100000002: "Canada Only",
};

function stateLabel(value: number | null | undefined): string {
  if (value == null) return "—";
  const found = STATES.find((s) => s.value === value);
  return found ? found.label : `Code ${value}`;
}

function displayValue(field: keyof SupplierFields, raw: unknown): string {
  if (raw == null || raw === "") return "—";
  if (field === "new_stateprovince" && typeof raw === "number") return stateLabel(raw);
  if (field === "new_servicearea" && typeof raw === "number")
    return SERVICE_AREA_LABEL[raw] ?? `Code ${raw}`;
  if (field === "new_about" || field === "new_discountdetails") {
    // HTML — show a plain-text preview
    const text = String(raw).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return text.length > 120 ? `${text.slice(0, 117)}…` : text;
  }
  if (field === "new_logourl" && typeof raw === "string") {
    return raw.startsWith("https://") && raw.includes("_staging/")
      ? "New file uploaded (will be saved on Submit)"
      : raw;
  }
  return String(raw);
}

interface FieldChange {
  field: keyof SupplierFields;
  label: string;
  before: string;
  after: string;
}

function diffFields(
  before: SupplierFields,
  after: SupplierFields,
  labels: Partial<Record<keyof SupplierFields, string>>,
): FieldChange[] {
  const changes: FieldChange[] = [];
  for (const key of Object.keys(labels) as (keyof SupplierFields)[]) {
    const a = before[key] ?? null;
    const b = after[key] ?? null;
    if (a !== b) {
      changes.push({
        field: key,
        label: labels[key]!,
        before: displayValue(key, a),
        after: displayValue(key, b),
      });
    }
  }
  return changes;
}

function ChangeTable({ changes }: { changes: FieldChange[] }) {
  if (changes.length === 0) {
    return <p className="text-muted mb-0">No changes.</p>;
  }
  return (
    <table className="table table-sm align-middle mb-0">
      <thead className="table-light">
        <tr>
          <th scope="col">Field</th>
          <th scope="col">Before</th>
          <th scope="col">After</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((c) => (
          <tr key={c.field}>
            <td>{c.label}</td>
            <td className="text-muted">{c.before}</td>
            <td className="fw-semibold">{c.after}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ContactSummary({ c }: { c: ContactRow }) {
  const name = [c.new_firstname, c.new_lastname].filter(Boolean).join(" ");
  return (
    <li>
      {c.is_primary && (
        <i
          className="fa-solid fa-star text-warning me-1"
          aria-label="Primary contact"
          title="Primary contact"
        />
      )}
      <strong>{name || "(unnamed)"}</strong>
      {c.is_primary ? <span className="text-warning ms-1 small">(Primary)</span> : null}
      {c.new_title ? ` — ${c.new_title}` : null}
      {c.new_email ? ` · ${c.new_email}` : null}
    </li>
  );
}

function DocumentSummary({ d }: { d: DocumentRow }) {
  return (
    <li>
      <strong>{d.new_name || "(untitled)"}</strong>
      {d.new_description ? ` — ${d.new_description}` : null}
    </li>
  );
}

function SectionCard({
  title,
  icon,
  editStep,
  jump,
  count,
  children,
}: {
  title: string;
  icon: string;
  editStep: number;
  jump: (n: number) => void;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="card mb-3">
      <header className="card-header d-flex justify-content-between align-items-center">
        <h3 className="h6 mb-0">
          <i className={`fa-solid ${icon} me-2`} aria-hidden="true" />
          {title}
          <span
            className={`badge ms-2 ${count > 0 ? "bg-primary" : "bg-light text-muted"}`}
            aria-label={`${count} pending change${count === 1 ? "" : "s"}`}
          >
            {count} change{count === 1 ? "" : "s"}
          </span>
        </h3>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => jump(editStep)}
        >
          <i className="fa-solid fa-pen me-1" aria-hidden="true" /> Edit
        </button>
      </header>
      <div className="card-body">{children}</div>
    </section>
  );
}

export default function Step5Review() {
  const { supplier, originalSupplier, contacts, originalContacts, documents, setStep } = useFormStore();

  const businessChanges = useMemo(
    () => diffFields(originalSupplier, supplier, SUPPLIER_FIELD_LABELS),
    [originalSupplier, supplier],
  );
  const productChanges = useMemo(
    () => diffFields(originalSupplier, supplier, PRODUCT_FIELD_LABELS),
    [originalSupplier, supplier],
  );

  const filesAdded = documents.filter((d) => d.action === "create");
  const filesUpdated = documents.filter((d) => d.action === "update");
  const filesRemoved = documents.filter((d) => d.action === "delete");
  const filesChangeCount = filesAdded.length + filesUpdated.length + filesRemoved.length;

  const contactsAdded = contacts.filter((c) => c.action === "create");
  const contactsUpdated = contacts.filter((c) => c.action === "update");
  const contactsRemoved = contacts.filter((c) => c.action === "delete");

  // Primary-contact change detection. No contact row's own fields change
  // when the primary toggle moves between contacts, so the action-flag
  // counts above miss it — compare against the prefill snapshot to surface
  // the change on the review screen.
  const originalPrimary = originalContacts.find((c) => c.is_primary);
  const currentPrimary = contacts.find((c) => c.is_primary && c.action !== "delete");
  const primaryChanged =
    (originalPrimary?.id ?? null) !== (currentPrimary?.id ?? null);
  const primaryFromLabel = originalPrimary
    ? [originalPrimary.new_firstname, originalPrimary.new_lastname].filter(Boolean).join(" ") || "(unnamed)"
    : "(none)";
  const primaryToLabel = currentPrimary
    ? [currentPrimary.new_firstname, currentPrimary.new_lastname].filter(Boolean).join(" ") || "(unnamed)"
    : "(none)";

  const contactsChangeCount =
    contactsAdded.length +
    contactsUpdated.length +
    contactsRemoved.length +
    (primaryChanged ? 1 : 0);

  const totalChanges =
    businessChanges.length + productChanges.length + filesChangeCount + contactsChangeCount;

  const isNewSupplier = originalSupplier.id == null;

  return (
    <div>
      <h2 className="brand-section-heading">
        <i className="fa-solid fa-list-check" aria-hidden="true" />
        Review &amp; Submit
      </h2>
      <p className="text-muted">
        {isNewSupplier
          ? "Review your new supplier registration below. Submit when you’re ready — your listing will be created and submitted documents will go to internal review."
          : totalChanges === 0
            ? "No changes detected. You can navigate back to make edits, or submit to leave your listing as-is."
            : `You have ${totalChanges} pending change${totalChanges === 1 ? "" : "s"}. Review each section below, then click Submit.`}
      </p>

      <SectionCard
        title="Business Information"
        icon="fa-building"
        editStep={1}
        jump={setStep}
        count={businessChanges.length}
      >
        <ChangeTable changes={businessChanges} />
      </SectionCard>

      <SectionCard
        title="Products & Services"
        icon="fa-box-open"
        editStep={2}
        jump={setStep}
        count={productChanges.length}
      >
        <ChangeTable changes={productChanges} />
      </SectionCard>

      <SectionCard
        title="Files"
        icon="fa-file-lines"
        editStep={3}
        jump={setStep}
        count={filesChangeCount}
      >
        {filesChangeCount === 0 ? (
          <p className="text-muted mb-0">No changes.</p>
        ) : (
          <>
            {filesAdded.length > 0 && (
              <>
                <h4 className="h6 text-success mt-1">
                  <i className="fa-solid fa-plus me-1" aria-hidden="true" />
                  Adding ({filesAdded.length})
                </h4>
                <ul className="mb-3">{filesAdded.map((d, i) => <DocumentSummary key={`a-${i}`} d={d} />)}</ul>
              </>
            )}
            {filesUpdated.length > 0 && (
              <>
                <h4 className="h6 text-primary mt-1">
                  <i className="fa-solid fa-pen me-1" aria-hidden="true" />
                  Updating ({filesUpdated.length})
                </h4>
                <ul className="mb-3">{filesUpdated.map((d, i) => <DocumentSummary key={`u-${i}`} d={d} />)}</ul>
              </>
            )}
            {filesRemoved.length > 0 && (
              <>
                <h4 className="h6 text-danger mt-1">
                  <i className="fa-solid fa-trash-can me-1" aria-hidden="true" />
                  Removing ({filesRemoved.length})
                </h4>
                <ul className="mb-0">{filesRemoved.map((d, i) => <DocumentSummary key={`r-${i}`} d={d} />)}</ul>
              </>
            )}
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Contacts"
        icon="fa-address-book"
        editStep={4}
        jump={setStep}
        count={contactsChangeCount}
      >
        {contactsChangeCount === 0 ? (
          <p className="text-muted mb-0">No changes.</p>
        ) : (
          <>
            {contactsAdded.length > 0 && (
              <>
                <h4 className="h6 text-success mt-1">
                  <i className="fa-solid fa-plus me-1" aria-hidden="true" />
                  Adding ({contactsAdded.length})
                </h4>
                <ul className="mb-3">{contactsAdded.map((c, i) => <ContactSummary key={`a-${i}`} c={c} />)}</ul>
              </>
            )}
            {contactsUpdated.length > 0 && (
              <>
                <h4 className="h6 text-primary mt-1">
                  <i className="fa-solid fa-pen me-1" aria-hidden="true" />
                  Updating ({contactsUpdated.length})
                </h4>
                <ul className="mb-3">{contactsUpdated.map((c, i) => <ContactSummary key={`u-${i}`} c={c} />)}</ul>
              </>
            )}
            {contactsRemoved.length > 0 && (
              <>
                <h4 className="h6 text-danger mt-1">
                  <i className="fa-solid fa-trash-can me-1" aria-hidden="true" />
                  Removing ({contactsRemoved.length})
                </h4>
                <ul className="mb-0">{contactsRemoved.map((c, i) => <ContactSummary key={`r-${i}`} c={c} />)}</ul>
              </>
            )}
            {primaryChanged && (
              <>
                <h4 className="h6 mt-3" style={{ color: "#003644" }}>
                  <i className="fa-solid fa-star text-warning me-1" aria-hidden="true" />
                  Primary contact changed
                </h4>
                <p className="mb-0 small">
                  <span className="text-muted">From:</span> {primaryFromLabel}
                  {" → "}
                  <span className="text-muted">To:</span> <strong>{primaryToLabel}</strong>
                </p>
              </>
            )}
          </>
        )}
      </SectionCard>

      <div className="alert alert-light border d-flex align-items-start gap-2 mt-3" role="note">
        <i className="fa-solid fa-circle-info mt-1" aria-hidden="true" />
        <div>
          <strong>What happens when you submit?</strong>
          <ul className="mb-0 ps-3 small">
            <li>Your business info, contacts, and document metadata update immediately.</li>
            <li>Newly uploaded documents enter <em>Under Review</em> and appear on your public listing only after internal approval.</li>
            <li>The Supplier Relations team is notified by email with the full details.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
